// ============================================================
// UPDATED AddSessionDetails
// Regular Session: Section (for students) → auto cameras
// Hostel Session: Hostel (for students) + Location (for cameras)
// ============================================================

import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import "./AddSessionT.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";

import {
  createSessionService,
  getCourseListService,
  get_students_by_section,
  bulkEnrollStudents,
  getCamerasByLocationService,
  getAllLocationsService,
  getAllHostels,
  getStudentsByHostel,
} from "../../services/api-service";
import styles from "../emotions-summary/index.module.css";

const Spinner = ({ size = 18, color = "#fff" }) => (
  <svg width={size} height={size} viewBox="0 0 50 50" aria-hidden>
    <circle
      cx="25"
      cy="25"
      r="20"
      stroke={color}
      strokeWidth="5"
      fill="none"
      strokeLinecap="round"
      strokeDasharray="31.4 31.4"
    >
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 25 25"
        to="360 25 25"
        dur="1s"
        repeatCount="indefinite"
      />
    </circle>
  </svg>
);

export default function AddSessionDetails() {
  const [courses, setCourses] = useState([]);
  const [values, setValues] = useState({
    start_date: "",
    end_date: "",
    course_name: "",
    location: "",
    session_id: "",
    start_time: "",
    end_time: "",
  });
  const [studentsData, setStudentsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedAdditionalSections, setSelectedAdditionalSections] = useState([]);
  const [studentsId, setStudentsId] = useState([]);
  const [bulkEnrollLoading, setBulkEnrollLoading] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [loadingCameras, setLoadingCameras] = useState(false);

  // ✅ Location state (for hostel sessions only)
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loadingLocations, setLoadingLocations] = useState(false);
  
  // Hostel state
  const [hostels, setHostels] = useState([]);
  const [selectedHostel, setSelectedHostel] = useState(null);
  const [loadingHostels, setLoadingHostels] = useState(false);
  
  const [sessionType, setSessionType] = useState('regular');

  const history = useHistory();
  const base64 = "data:image/png;base64";

  const sessionTypeOptions = [
    { label: '📍 Regular Session', value: 'regular' },
    { label: '🏨 Hostel Session', value: 'hostel' }
  ];

  useEffect(() => {
    fetchCourses();
    fetchAllLocations();
  }, []);

  const fetchCourses = async () => {
    try {
      const res = await getCourseListService();
      const formatted = (res?.data || []).map((el) => ({
        label: el.course_name,
        value: el.course_name,
      }));
      setCourses(formatted);
    } catch (err) {
      toast.error("Failed to load courses");
    }
  };

  const fetchAllLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await getAllLocationsService();
      if (response?.data) {
        const locationOpts = response.data
          .filter((loc) => loc)
          .map((loc) => ({
            label: loc,
            value: loc,
          }));
        setLocationOptions(locationOpts);
        setSections(locationOpts);
      }
    } catch (err) {
      try {
        const response = await get_students_by_section();
        
        let studentsList = [];
        if (Array.isArray(response?.students)) {
          studentsList = response.students;
        } else if (Array.isArray(response?.data?.students)) {
          studentsList = response.data.students;
        } else if (Array.isArray(response?.data)) {
          studentsList = response.data;
        }
        
        if (studentsList.length > 0) {
          const uniqueSections = [
            ...new Map(
              studentsList.map((student) => [student.section, student.section])
            ).values(),
          ];
          const sectionOptions = uniqueSections
            .filter((sec) => sec)
            .map((sec) => ({
              label: sec,
              value: sec,
            }));
          setLocationOptions(sectionOptions);
          setSections(sectionOptions);
        }
      } catch (innerErr) {
        console.error("Fallback error:", innerErr);
      }
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchAllHostels = async () => {
    setLoadingHostels(true);
    try {
      const res = await getAllHostels();
      
      let hostelList = [];
      if (res?.data?.data && Array.isArray(res.data.data)) {
        hostelList = res.data.data;
      } else if (res?.data && Array.isArray(res.data)) {
        hostelList = res.data;
      }
      
      const hostelOptions = hostelList.map((hostel) => ({
        label: hostel,
        value: hostel,
      }));

      setHostels(hostelOptions);
      setSelectedHostel(null);
    } catch (err) {
      console.error("Error fetching hostels:", err);
      setHostels([]);
      toast.error("Failed to load hostels");
    } finally {
      setLoadingHostels(false);
    }
  };

  // ✅ Fetch cameras by location (for hostel sessions)
  const fetchCamerasByLocation = async (location) => {
    if (!location) {
      setCameras([]);
      setSelectedCamera(null);
      return;
    }

    setLoadingCameras(true);
    try {
      const res = await getCamerasByLocationService(location);
      const cameraOptions = (res?.data || []).map((cam) => ({
        label: cam.camera_name,
        value: cam.camera_id,
      }));

      setCameras(cameraOptions);

      if (cameraOptions.length > 0) {
        setSelectedCamera(cameraOptions[0]);
      } else {
        setSelectedCamera(null);
        toast.warn(`⚠️ No cameras found for location: ${location}`);
      }
    } catch (err) {
      console.error("Error fetching cameras:", err);
      setCameras([]);
      setSelectedCamera(null);
      toast.error("Failed to load cameras");
    } finally {
      setLoadingCameras(false);
    }
  };

  const handleChange = (event, name) => {
    const value = event?.value ?? event?.target?.value;
    setValues((prev) => ({
      ...prev,
      [name]: isNaN(value) ? value : Number(value),
    }));
  };

  // ✅ Handle location selection (hostel only)
  const handleChangeLocation = async (selected) => {
    setSelectedLocation(selected);
    
    if (selected) {
      setValues((prev) => ({
        ...prev,
        location: selected.value,
      }));
      
      await fetchCamerasByLocation(selected.value);
    } else {
      setValues((prev) => ({
        ...prev,
        location: "",
      }));
      setCameras([]);
      setSelectedCamera(null);
    }
  };

  const handleSessionTypeChange = async (opt) => {
    setSessionType(opt.value);
    setStudentsData([]);
    setStudentsId([]);
    setSelectedSection(null);
    setSelectedHostel(null);
    setSelectedAdditionalSections([]);
    setSelectedLocation(null);
    setCameras([]);
    setSelectedCamera(null);
    setValues((prev) => ({
      ...prev,
      location: "",
    }));
    
    // ✅ Auto-fetch hostels when switching to hostel type
    if (opt.value === 'hostel') {
      await fetchAllHostels();
    }
  };

  // ✅ Handle section selection (regular sessions - auto cameras)
  const handleChangeSection = async (selected) => {
    setSelectedSection(selected);
    setSelectedAdditionalSections([]);
    setStudentsData([]);
    setStudentsId([]);
    setCameras([]);
    setSelectedCamera(null);

    if (!selected) {
      setValues((prev) => ({
        ...prev,
        location: "",
      }));
      return;
    }

    // ✅ Auto-fetch cameras for section
    setValues((prev) => ({
      ...prev,
      location: selected.value,
    }));

    try {
      setLoadingStudents(true);
      const response = await get_students_by_section(selected.value);

      let studentsList = [];
      if (Array.isArray(response?.students)) {
        studentsList = response.students;
      } else if (Array.isArray(response?.data?.students)) {
        studentsList = response.data.students;
      } else if (Array.isArray(response?.data)) {
        studentsList = response.data;
      }

      if (studentsList.length > 0) {
        setStudentsData(studentsList);
        setStudentsId([]);
        toast.success(`✅ Loaded ${studentsList.length} students from ${selected.label}`);
      } else {
        setStudentsData([]);
        setStudentsId([]);
        toast.warn(`⚠️ No students found in ${selected.label}`);
      }
    } catch (err) {
      console.error("Error auto-fetching students:", err);
      toast.error("Failed to load students");
      setStudentsData([]);
    } finally {
      setLoadingStudents(false);
    }

    // ✅ Auto-fetch cameras for section
    await fetchCamerasByLocation(selected.value);
  };

  // ✅ Handle hostel selection (hostel sessions - manual location)
  const handleHostelChange = async (selected) => {
    setSelectedHostel(selected);
    setStudentsId([]);
    setSelectedLocation(null);
    setCameras([]);
    setSelectedCamera(null);

    if (!selected) {
      setStudentsData([]);
      setValues((prev) => ({
        ...prev,
        location: "",
      }));
      return;
    }

    setLoadingStudents(true);
    try {
      const res = await getStudentsByHostel(selected.value);
      
      let studentsList = [];
      if (res?.data?.students && Array.isArray(res.data.students)) {
        studentsList = res.data.students;
      } else if (res?.data && Array.isArray(res.data)) {
        studentsList = res.data;
      }

      if (studentsList.length > 0) {
        setStudentsData(studentsList);
        toast.success(`✅ Loaded ${studentsList.length} students from ${selected.label}`);
      } else {
        setStudentsData([]);
        toast.warn(`⚠️ No students found in ${selected.label}`);
      }
    } catch (err) {
      console.error("Error fetching hostel students:", err);
      toast.error("Failed to load hostel students");
      setStudentsData([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleAdditionalSectionsChange = async (selectedOptions) => {
    setSelectedAdditionalSections(selectedOptions || []);

    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    setLoadingStudents(true);
    try {
      let allStudents = [];

      for (const section of selectedOptions) {
        const response = await get_students_by_section(section.value);

        let studentsList = [];
        if (Array.isArray(response?.students)) {
          studentsList = response.students;
        } else if (Array.isArray(response?.data?.students)) {
          studentsList = response.data.students;
        } else if (Array.isArray(response?.data)) {
          studentsList = response.data;
        }

        allStudents = [...allStudents, ...studentsList];
      }

      const existingIds = new Set(studentsData.map(s => s.student_id));
      const newStudents = allStudents.filter(s => !existingIds.has(s.student_id));

      const mergedStudents = [...studentsData, ...newStudents];

      setStudentsData(mergedStudents);
      toast.success(`✅ Added ${newStudents.length} students from selected sections`);
    } catch (err) {
      console.error("Error fetching students from additional sections:", err);
      toast.error("Failed to load students from selected sections");
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleFilterStudent = async (e) => {
    e?.preventDefault?.();

    if (sessionType === 'regular' && !selectedSection) {
      toast.warn("Please select a section first");
      return;
    }

    if (sessionType === 'hostel' && !selectedHostel) {
      toast.warn("Please select a hostel first");
      return;
    }

    setLoadingStudents(true);
    try {
      const locationValue = sessionType === 'regular' ? selectedSection.value : selectedHostel.value;
      
      if (sessionType === 'regular') {
        const response = await get_students_by_section(locationValue);

        let studentsList = [];
        if (Array.isArray(response?.students)) {
          studentsList = response.students;
        } else if (Array.isArray(response?.data?.students)) {
          studentsList = response.data.students;
        } else if (Array.isArray(response?.data)) {
          studentsList = response.data;
        }

        if (studentsList.length > 0) {
          setStudentsData(studentsList);
          setStudentsId([]);
          toast.success(`✅ Loaded ${studentsList.length} students`);
        } else {
          setStudentsData([]);
          setStudentsId([]);
          toast.warn(`⚠️ No students found`);
        }
      } else {
        const res = await getStudentsByHostel(locationValue);
        
        let studentsList = [];
        if (res?.data?.students && Array.isArray(res.data.students)) {
          studentsList = res.data.students;
        } else if (res?.data && Array.isArray(res.data)) {
          studentsList = res.data;
        }

        if (studentsList.length > 0) {
          setStudentsData(studentsList);
          setStudentsId([]);
          toast.success(`✅ Loaded ${studentsList.length} students`);
        } else {
          setStudentsData([]);
          setStudentsId([]);
          toast.warn(`⚠️ No students found`);
        }
      }
    } catch (err) {
      console.error("Error fetching students:", err);
      toast.error("Failed to get students");
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleStudentSelect = (event, studentId) => {
    if (event.target.checked) {
      setStudentsId((pre) => [...pre, studentId]);
    } else {
      setStudentsId((prev) => prev.filter((el) => el !== studentId));
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setStudentsId(studentsData.map((d) => d.student_id));
    } else {
      setStudentsId([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const validationErrors = [];
      if (!values.course_name) validationErrors.push("Please select a course");
      if (!values.start_date) validationErrors.push("Please select start date");
      if (!values.end_date) validationErrors.push("Please select end date");
      if (!values.start_time) validationErrors.push("Please select start time");
      if (!values.end_time) validationErrors.push("Please select end time");
      if (!selectedCamera) validationErrors.push("Please select a camera");
      if (!values.session_id) validationErrors.push("Please enter a session ID");
      if (studentsId.length === 0) validationErrors.push("Please select at least one student");

      if (sessionType === 'regular' && !selectedSection) {
        validationErrors.push("Please select a section");
      }
      if (sessionType === 'hostel' && !selectedHostel) {
        validationErrors.push("Please select a hostel");
      }
      if (sessionType === 'hostel' && !selectedLocation) {
        validationErrors.push("Please select a location for cameras");
      }

      if (validationErrors.length > 0) {
        toast.warn(validationErrors[0]);
        setLoading(false);
        return;
      }

      const selectedStudents = studentsData.filter((s) =>
        studentsId.includes(s.student_id)
      );

      const additionalSectionsList = selectedAdditionalSections.map(s => s.value);
      const sessionSection = sessionType === 'hostel' ? selectedHostel.value : selectedSection.value;

      const response = await createSessionService({
        course_name: values.course_name,
        location: values.location,
        camera_id: selectedCamera.value,
        start_date: values.start_date,
        end_date: values.end_date,
        start_time: values.start_time,
        end_time: values.end_time,
        session_id: String(values.session_id),
        student_ids: selectedStudents.map((s) => String(s.student_id)),
        session_type: sessionType,
        hostel_session_name: sessionType === 'hostel' ? selectedHostel.value : null,
        first_name: selectedStudents.map((s) => s.first_name),
        last_name: selectedStudents.map((s) => s.last_name),
        section: selectedStudents.map((s) => s.section),
        email: selectedStudents.map((s) => s.email),
        additional_sections: additionalSectionsList,
      });

      toast.success(response?.data || "✅ Session created successfully");

      setValues({
        start_date: "",
        end_date: "",
        course_name: "",
        location: "",
        session_id: "",
        start_time: "",
        end_time: "",
      });
      setStudentsId([]);
      setStudentsData([]);
      setSelectedSection(null);
      setSelectedAdditionalSections([]);
      setSelectedCamera(null);
      setSelectedHostel(null);
      setSelectedLocation(null);
      setSessionType('regular');

      setTimeout(() => {
        history.push("AddSession");
      }, 1000);
    } catch (error) {
      const message =
        error?.response?.data?.detail || error?.message || "Failed to create session";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEnroll = async (e) => {
    e.preventDefault();
    setBulkEnrollLoading(true);

    try {
      const validationErrors = [];
      if (!values.course_name) validationErrors.push("Please select a course");
      if (!values.start_date) validationErrors.push("Please select start date");
      if (!values.end_date) validationErrors.push("Please select end date");
      if (!values.start_time) validationErrors.push("Please select start time");
      if (!values.end_time) validationErrors.push("Please select end time");
      if (!selectedCamera) validationErrors.push("Please select a camera");
      if (!values.session_id) validationErrors.push("Please enter a session ID");

      if (sessionType === 'regular' && !selectedSection) {
        validationErrors.push("Please select a section");
      }
      if (sessionType === 'hostel' && !selectedHostel) {
        validationErrors.push("Please select a hostel");
      }
      if (sessionType === 'hostel' && !selectedLocation) {
        validationErrors.push("Please select a location for cameras");
      }

      if (validationErrors.length > 0) {
        toast.warn(validationErrors[0]);
        setBulkEnrollLoading(false);
        return;
      }

      const result = await bulkEnrollStudents({
        section: sessionType === 'hostel' ? selectedHostel.value : selectedSection.value,
        course_name: values.course_name,
        session_id: String(values.session_id),
        location: values.location,
        camera_id: selectedCamera.value,
        start_date: values.start_date,
        end_date: values.end_date,
        start_time: values.start_time,
        end_time: values.end_time,
      });

      toast.success(
        `✅ Bulk enrolled ${result.enrolled_count} students (skipped: ${result.skipped_count})`
      );

      setValues({
        start_date: "",
        end_date: "",
        course_name: "",
        location: "",
        session_id: "",
        start_time: "",
        end_time: "",
      });
      setStudentsId([]);
      setStudentsData([]);
      setSelectedSection(null);
      setSelectedAdditionalSections([]);
      setSelectedCamera(null);
      setSelectedHostel(null);
      setSelectedLocation(null);
      setSessionType('regular');

      setTimeout(() => {
        history.push("AddSession");
      }, 1000);
    } catch (error) {
      toast.error(error.message || "Failed to bulk enroll students");
    } finally {
      setBulkEnrollLoading(false);
    }
  };

  return (
    <>
      {(loading || bulkEnrollLoading) && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#222",
              padding: "16px 20px",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <Spinner size={22} color="#fff" />
            <div style={{ color: "#fff", fontWeight: 600 }}>
              {bulkEnrollLoading ? "Bulk enrolling..." : "Saving session..."}
            </div>
          </div>
        </div>
      )}

      <main className="camcont m-4">
        <section className="student-panel">
          <div className="input-section">
            <div className="label-input">
              <label className="label-fields large-text">📌 Session ID</label>
              <input
                type="text"
                value={values.session_id}
                name="session_id"
                onChange={(event) => handleChange(event, event.target.name)}
                className="input-fields"
                placeholder="e.g., 1101"
              />
            </div>

            <div className="label-input">
              <label className="label-fields large-text">📚 Course</label>
              <div className="select-wrapper">
                <Select
                  options={courses}
                  onChange={(event) => handleChange(event, "course_name")}
                  placeholder="Select Course"
                  value={courses.find((c) => c.value === values.course_name) || null}
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 4,
                    colors: {
                      ...theme.colors,
                      primary25: "rgb(51 65 85 / 63%)",
                      primary: "rgb(51 65 85)",
                    },
                  })}
                />
              </div>
            </div>

            <div className="label-input">
              <label className="label-fields large-text">📅 Start Date</label>
              <input
                className="input-fields"
                type="date"
                name="start_date"
                value={values.start_date}
                onChange={(event) => handleChange(event, event.target.name)}
              />
            </div>

            <div className="label-input">
              <label className="label-fields large-text">📅 End Date</label>
              <input
                className="input-fields"
                type="date"
                name="end_date"
                value={values.end_date}
                onChange={(event) => handleChange(event, event.target.name)}
              />
            </div>

            <div className="label-input">
              <label className="label-fields large-text">🕐 Start Time</label>
              <input
                className="input-fields"
                type="time"
                name="start_time"
                value={values.start_time}
                onChange={(event) => handleChange(event, event.target.name)}
              />
            </div>

            <div className="label-input">
              <label className="label-fields large-text">🕐 End Time</label>
              <input
                className="input-fields"
                type="time"
                name="end_time"
                value={values.end_time}
                onChange={(event) => handleChange(event, event.target.name)}
              />
            </div>

            {/* Session Type Selector */}
            <div className="label-input">
              <label className="label-fields large-text">🎯 Session Type</label>
              <div className="select-wrapper">
                <Select
                  options={sessionTypeOptions}
                  value={sessionTypeOptions.find(s => s.value === sessionType) || sessionTypeOptions[0]}
                  onChange={handleSessionTypeChange}
                  placeholder="Select session type"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 4,
                    colors: {
                      ...theme.colors,
                      primary25: "rgb(51 65 85 / 63%)",
                      primary: "rgb(51 65 85)",
                    },
                  })}
                />
              </div>
            </div>

            {/* Regular Session: Section */}
            {sessionType === 'regular' && (
              <div className="label-input">
                <label className="label-fields large-text">👥 Section</label>
                <div className="select-wrapper">
                  <Select
                    options={sections}
                    onChange={handleChangeSection}
                    value={selectedSection}
                    placeholder="Select Section"
                    theme={(theme) => ({
                      ...theme,
                      borderRadius: 4,
                      colors: {
                        ...theme.colors,
                        primary25: "rgb(51 65 85 / 63%)",
                        primary: "rgb(51 65 85)",
                      },
                    })}
                  />
                </div>
              </div>
            )}

            {/* Hostel Session: Hostel + Location */}
            {sessionType === 'hostel' && (
              <>
                <div className="label-input">
                  <label className="label-fields large-text">🏨 Hostel</label>
                  {loadingHostels ? (
                    <div style={{ padding: "10px", textAlign: "center", color: "#666" }}>
                      <Spinner size={14} color="#333" /> Loading hostels...
                    </div>
                  ) : hostels.length > 0 ? (
                    <div className="select-wrapper">
                      <Select
                        options={hostels}
                        onChange={handleHostelChange}
                        value={selectedHostel}
                        placeholder="Select Hostel"
                        theme={(theme) => ({
                          ...theme,
                          borderRadius: 4,
                          colors: {
                            ...theme.colors,
                            primary25: "rgb(51 65 85 / 63%)",
                            primary: "rgb(51 65 85)",
                          },
                        })}
                      />
                    </div>
                  ) : (
                    <div className="status-box warning">
                      ⚠️ No hostels found in system
                    </div>
                  )}
                </div>

                {/* ✅ Location selector for Hostel sessions only */}
                <div className="label-input">
                  <label className="label-fields large-text">📍 Location (For Cameras)</label>
                  {loadingLocations ? (
                    <div style={{ padding: "10px", textAlign: "center", color: "#666" }}>
                      <Spinner size={14} color="#333" /> Loading locations...
                    </div>
                  ) : locationOptions.length > 0 ? (
                    <div className="select-wrapper">
                      <Select
                        options={locationOptions}
                        onChange={handleChangeLocation}
                        value={selectedLocation}
                        placeholder="Select Location"
                        theme={(theme) => ({
                          ...theme,
                          borderRadius: 4,
                          colors: {
                            ...theme.colors,
                            primary25: "rgb(51 65 85 / 63%)",
                            primary: "rgb(51 65 85)",
                          },
                        })}
                      />
                    </div>
                  ) : (
                    <div className="status-box warning">
                      ⚠️ No locations found in system
                    </div>
                  )}
                  <small style={{ color: "#666", marginTop: "0.25rem" }}>
                    Select location to load available cameras for this session
                  </small>
                </div>
              </>
            )}

            <div className="label-input">
              <label className="label-fields large-text">➕ Additional Sections</label>
              <div className="select-wrapper">
                <Select
                  isMulti
                  options={sections.filter(
                    (s) => !selectedSection || s.value !== selectedSection.value
                  )}
                  onChange={handleAdditionalSectionsChange}
                  value={selectedAdditionalSections}
                  placeholder="Select additional sections (optional)"
                  isDisabled={!selectedSection || sessionType === 'hostel'}
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 4,
                    colors: {
                      ...theme.colors,
                      primary25: "rgb(51 65 85 / 63%)",
                      primary: "rgb(51 65 85)",
                    },
                  })}
                />
              </div>
              <small style={{ color: "#666", marginTop: "0.25rem" }}>
                {sessionType === 'hostel' 
                  ? "Not available for hostel sessions" 
                  : "Select additional sections to load students from multiple locations"}
              </small>
            </div>

            <div className="label-input">
              <label className="label-fields large-text">📹 Camera</label>
              {loadingCameras ? (
                <div style={{ padding: "10px", textAlign: "center" }}>
                  <Spinner size={14} color="#333" /> Loading cameras...
                </div>
              ) : cameras.length > 0 ? (
                <>
                  <div className="status-box success">
                    ✅ {cameras.length} available
                  </div>
                  <div className="select-wrapper">
                    <Select
                      options={cameras}
                      onChange={setSelectedCamera}
                      value={selectedCamera}
                      placeholder="Select Camera"
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 4,
                        colors: {
                          ...theme.colors,
                          primary25: "rgb(51 65 85 / 63%)",
                          primary: "rgb(51 65 85)",
                        },
                      })}
                    />
                  </div>
                </>
              ) : (
                <div className="status-box warning">
                  ⚠️ {sessionType === 'regular' && selectedSection ? "No cameras available for this section" : sessionType === 'hostel' && selectedLocation ? "No cameras available for this location" : "Select section or location to load cameras"}
                </div>
              )}
            </div>

            <div className="label-input button-row">
              <button
                type="button"
                onClick={handleSubmit}
                className="submit-button info"
                disabled={loading || bulkEnrollLoading || !selectedCamera}
              >
                {loading ? <Spinner size={14} color="#fff" /> : "➕"}
                {loading ? "Saving..." : "Add Session"}
              </button>

              <button
                type="button"
                onClick={handleBulkEnroll}
                className="submit-button success"
                disabled={loading || bulkEnrollLoading || !selectedCamera}
              >
                {bulkEnrollLoading ? <Spinner size={14} color="#fff" /> : "👥"}
                {bulkEnrollLoading ? "Enrolling..." : "Bulk Enroll"}
              </button>

              <button
                type="button"
                className="submit-button myBtn"
                onClick={handleFilterStudent}
                disabled={loadingStudents || (sessionType === 'regular' && !selectedSection) || (sessionType === 'hostel' && !selectedHostel)}
              >
                {loadingStudents ? <Spinner size={14} color="#fff" /> : "🔍"}
                {loadingStudents ? "Loading..." : "Get Students"}
              </button>
            </div>
          </div>
        </section>

        {studentsData.length > 0 && (
          <section className="student-panel">
            <h2 style={{ margin: "0 0 1rem 0", color: "#102747", fontSize: "16px" }}>
              👥 Students ({studentsId.length}/{studentsData.length} selected)
              {sessionType === 'hostel' && selectedHostel && <span style={{ marginLeft: "1rem", color: "#666" }}>🏨 {selectedHostel.label}</span>}
              {sessionType === 'regular' && selectedSection && <span style={{ marginLeft: "1rem", color: "#666" }}>📍 {selectedSection.label}</span>}
              {selectedLocation && <span style={{ marginLeft: "1rem", color: "#666", fontSize: "12px" }}>📍 Loc: {selectedLocation.label}</span>}
            </h2>

            <div className="tableContainer">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: "50px" }}>
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={
                          studentsId.length === studentsData.length &&
                          studentsData.length > 0
                        }
                      />
                    </th>
                    <th>Image</th>
                    <th>Student ID</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Section</th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {studentsData.map((d) => {
                    const isSelected = studentsId.includes(d.student_id);
                    return (
                      <tr
                        key={d.student_id}
                        className={isSelected ? "selected-row" : ""}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleStudentSelect(e, d.student_id)}
                          />
                        </td>
                        <td className="es-th">
                          <img
                            src={`${base64},${d.image}`}
                            alt="student"
                          />
                        </td>
                        <td className="es-th">{d.student_id}</td>
                        <td className="es-th">{d.first_name}</td>
                        <td className="es-th">{d.last_name}</td>
                        <td className="es-th">{d.section}</td>
                        <td className="es-th">{d.email}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {!studentsData.length && 
          ((sessionType === 'regular' && selectedSection) || (sessionType === 'hostel' && selectedHostel)) && (
          <section className="student-panel">
            <div className="empty-state">
              <div className="empty-state-title">📭 No students loaded</div>
              <div className="empty-state-text">
                Click "Get Students" to load students
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}